import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 active:bg-primary/75",
        destructive:
          "bg-destructive border text-destructive-foreground shadow-xs hover:bg-destructive/70 active:bg-destructive/50",
        outline:
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground active:bg-accent/75",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 active:bg-secondary/60",
        ghost:
          "hover:bg-primary hover:text-primary-foreground active:bg-primary/75",
        link: "text-primary underline-offset-4 hover:underline active:text-primary/75",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

/**
 * This is a simple button group, it just puts the buttons in a <uL> and adds a gap between each button.
 * For more advanced use cases, see the dedicated button-group component! (@repo/ui/button-group)
 */
const ButtonGroup = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(({ children, className, ...props }, ref) => {
  if (React.Children.count(children) === 0) {
    return null;
  }

  return (
    <ul className={cn("flex gap-2", className)} {...props} ref={ref}>
      {React.Children.map(children, (child, index) => (
        // eslint-disable-next-line react/no-array-index-key -- there is no better key we can automatically extract
        <li key={index} className="*:w-full">
          {child}
        </li>
      ))}
    </ul>
  );
});

ButtonGroup.displayName = "ButtonGroup";

export { Button, buttonVariants, ButtonGroup };
